/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Email Outbox — every message queued, sent or failed.
 *
 * Follows the Notification Masters list pattern so the two screens read as one
 * section: PermissionGuard → PageHeader (collapses when embedded in the tab
 * group) → a bordered card holding SaasDataTable, with the filters in
 * `toolbarStart` and the record count / export / refresh in `toolbarEnd`.
 *
 * Timestamps go through formatUtcInIst: the API serialises naive UTC with no Z,
 * which `new Date(...)` reads as LOCAL time and renders 5:30 early.
 */
import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Toast } from 'sharedUi/Toast';
import { Tag } from 'sharedUi/Tag';
import { Tooltip } from 'sharedUi/Tooltip';
import { Button } from 'sharedUi/Button';
import { Dropdown } from 'sharedUi/Dropdown';
import { RefreshButton } from 'sharedUi/RefreshButton';
import { ExportButton } from 'sharedUi/ExportButton';
import { useAppNavigate } from '../../hooks/useAppNavigate';
import PageHeader from '../../components/common/page-header';
import PermissionGuard from '../../components/guards/PermissionGuard';
import SaasDataTable, { type SaasColumn } from '../../components/common/saas-data-table';
import { extractErrorMessage } from '../../utils/error-handler';
import { formatUtcInIst, parseUtcInstant } from '../../utils/timezone.util';
import {
  emailService,
  EMAIL_STATUSES,
  type EmailStatus,
  type OutboxEntry,
} from '../../services/email.service';

/** Status -> Tag severity. There is no DELIVERED state by design: SMTP
 *  acknowledges submission, not delivery, so SENT is as strong as it gets. */
const STATUS_SEVERITY: Record<EmailStatus, 'success' | 'info' | 'warning' | 'danger' | 'secondary'> = {
  SENT: 'success',
  SENDING: 'info',
  QUEUED: 'warning',
  FAILED: 'danger',
  CANCELLED: 'secondary',
};

const formatDate = (value?: string | null) =>
  value
    ? formatUtcInIst(value, {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      })
    : '—';

/** Compact elapsed time, e.g. "2s", "4m", "1h 12m". */
const humanGap = (ms: number): string => {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
};

/** How long a message sat in the queue before it went out. */
const queueLatency = (row: OutboxEntry): string | null => {
  const a = parseUtcInstant(row.createdAt);
  const b = parseUtcInstant(row.sentAt);
  if (!a || !b) return null;
  return humanGap(b.getTime() - a.getTime());
};

export default function EmailOutboxList({ embedded = false }: { embedded?: boolean }) {
  const navigate = useAppNavigate();
  const toast = useRef<any>(null);

  const [rows, setRows] = useState<OutboxEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [first, setFirst] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [status, setStatus] = useState<EmailStatus | ''>('');
  const [search, setSearch] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [sortField, setSortField] = useState<string | null>('createdAt');
  const [sortOrder, setSortOrder] = useState<1 | -1 | null>(-1);

  const page = useMemo(() => Math.floor(first / pageSize) + 1, [first, pageSize]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await emailService.listOutbox({ status, search, page, pageSize });
      setRows(result.data || []);
      setTotal(result.total || 0);
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'Could not load outbox',
        detail: extractErrorMessage(error),
      });
    } finally {
      setLoading(false);
    }
  }, [status, search, page, pageSize]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Sort the current page client-side; the API pages, it does not sort. */
  const sortedRows = useMemo(() => {
    if (!sortField || !sortOrder) return rows;
    const val = (r: OutboxEntry) => {
      const v = (r as any)[sortField];
      if (sortField === 'to') return (r.to || [])[0] || '';
      return v ?? '';
    };
    return [...rows].sort((a, b) => {
      const x = val(a), y = val(b);
      if (x === y) return 0;
      return (x > y ? 1 : -1) * sortOrder;
    });
  }, [rows, sortField, sortOrder]);

  const handleResend = useCallback(async (row: OutboxEntry) => {
    setBusyId(row.id);
    try {
      await emailService.resend(row.id);
      toast.current?.show({
        severity: 'success',
        summary: 'Re-queued',
        detail: 'A fresh copy was queued; the original is kept for audit.',
      });
      await load();
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'Resend failed',
        detail: extractErrorMessage(error),
      });
    } finally {
      setBusyId(null);
    }
  }, [load]);

  const handleCancel = useCallback(async (row: OutboxEntry) => {
    setBusyId(row.id);
    try {
      await emailService.cancel(row.id);
      toast.current?.show({ severity: 'success', summary: 'Cancelled' });
      await load();
    } catch (error) {
      // 400 here usually means the worker already picked it up.
      toast.current?.show({
        severity: 'warn',
        summary: 'Could not cancel',
        detail: extractErrorMessage(error),
      });
    } finally {
      setBusyId(null);
    }
  }, [load]);

  const columns: SaasColumn<OutboxEntry>[] = useMemo(
    () => [
      // Status carries the whole lifecycle, not just a label: the retry count
      // and the next scheduled attempt belong with it, not in a column of their own.
      {
        field: 'status',
        header: 'Status',
        sortable: true,
        style: { width: '150px' },
        body: (row: OutboxEntry) => (
          <div className="flex flex-col items-start gap-1">
            <Tag value={row.status} severity={STATUS_SEVERITY[row.status] ?? 'secondary'} />
            {row.attempts > 1 && (
              <span className="font-mono text-[10.5px] text-ink-muted">
                {row.attempts} attempts
              </span>
            )}
            {row.status === 'QUEUED' && row.nextAttemptAt && (
              <span className="font-mono text-[10.5px] text-amber-600 dark:text-amber-400">
                retry {formatUtcInIst(row.nextAttemptAt, { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
          </div>
        ),
      },
      // The message itself: subject leads, everyone it went to sits underneath,
      // and a failure reason — previously buried in a title attribute — is shown
      // inline, since that is the reason anyone opens an outbox.
      {
        field: 'subject',
        header: 'Message',
        sortable: true,
        body: (row: OutboxEntry) => {
          const to = row.to || [];
          const extra = (row.cc?.length || 0) + (row.bcc?.length || 0);
          return (
            <div className="min-w-0">
              <div className="truncate text-[13px] font-semibold text-ink">
                {row.subject || <span className="italic text-gray-400">No subject</span>}
              </div>

              <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11.5px] text-ink-muted">
                <span className="truncate" title={to.join(', ')}>
                  {to[0] || '—'}
                  {to.length > 1 && <span className="ml-1 text-gray-400">+{to.length - 1}</span>}
                </span>
                {extra > 0 && (
                  <span title={`${row.cc?.length || 0} cc · ${row.bcc?.length || 0} bcc`}>
                    +{extra} copied
                  </span>
                )}
                {row.attachments?.length > 0 && (
                  <span
                    className="inline-flex items-center gap-1"
                    title={row.attachments.map((a) => a.filename).join(', ')}
                  >
                    <i className="pi pi-paperclip text-[10px]" />
                    {row.attachments.length}
                  </span>
                )}
              </div>

              {row.status === 'FAILED' && row.lastError && (
                <div
                  className="mt-1 truncate font-mono text-[11px] text-rose-600 dark:text-rose-400"
                  title={row.lastError}
                >
                  {row.lastError}
                </div>
              )}
            </div>
          );
        },
      },
      // One timing column instead of two near-identical ones. What matters is
      // when it was queued and how long it took to leave.
      {
        field: 'createdAt',
        header: 'Timing',
        sortable: true,
        style: { width: '200px' },
        body: (row: OutboxEntry) => {
          const latency = queueLatency(row);
          return (
            <div className="flex flex-col gap-0.5">
              <span className="tabular-nums text-[12.5px] text-ink">
                {formatDate(row.createdAt)}
              </span>
              <span className="font-mono text-[10.5px] text-ink-muted">
                {latency
                  ? `sent ${latency} later`
                  : row.status === 'QUEUED'
                    ? 'waiting in queue'
                    : '—'}
              </span>
            </div>
          );
        },
      },
      // Provenance: whether this came from a notification or a manual compose,
      // and which provider carried it. Both were in the payload and never shown.
      {
        field: 'source',
        header: 'Source',
        sortable: true,
        style: { width: '150px' },
        body: (row: OutboxEntry) => (
          <div className="flex flex-col gap-0.5">
            <span className="font-mono text-[11px] text-gray-700 dark:text-gray-300">
              {row.source || '—'}
            </span>
            {row.provider && (
              <span className="font-mono text-[10px] text-ink-muted">
                {row.provider}
              </span>
            )}
          </div>
        ),
      },
      {
        field: 'actions',
        header: 'Actions',
        align: 'center',
        hideable: false,
        style: { width: '150px' },
        body: (row: OutboxEntry) => (
          <div className="flex justify-center gap-1">
            <button aria-label="View"
              className="rounded p-2 transition-colors hover:bg-gray-100 dark:hover:bg-gray-700"
              onClick={() => navigate(`/email/outbox/${row.id}`)}
              data-testid="EmailOutboxList.Action.View"
              data-pr-tooltip="View"
            >
              <i className="pi pi-eye text-green-600 dark:text-green-400" style={{ fontSize: '1.125rem' }} />
            </button>
            <Tooltip target="[data-pr-tooltip]" />

            {row.status === 'QUEUED' && (
              <button aria-label="Cancel"
                className="rounded p-2 transition-colors hover:bg-red-50 disabled:opacity-40 dark:hover:bg-red-900/20"
                disabled={busyId === row.id}
                onClick={() => void handleCancel(row)}
                data-testid="EmailOutboxList.Action.Cancel"
                data-pr-tooltip="Cancel"
              >
                <i className="pi pi-times text-red-600" style={{ fontSize: '1.125rem' }} />
              </button>
            )}

            {(row.status === 'FAILED' || row.status === 'SENT') && (
              <button aria-label="Rerun"
                className="rounded p-2 transition-colors hover:bg-blue-50 disabled:opacity-40 dark:hover:bg-blue-900/20"
                disabled={busyId === row.id}
                onClick={() => void handleResend(row)}
                data-testid="EmailOutboxList.Action.Resend"
                data-pr-tooltip="Resend"
              >
                <i className="pi pi-replay text-blue-600" style={{ fontSize: '1.125rem' }} />
              </button>
            )}
          </div>
        ),
      },
    ],
    [busyId, navigate, handleResend, handleCancel],
  );

  const exportColumns = useMemo(
    () => [
      { field: 'status', header: 'Status' },
      { field: 'subject', header: 'Subject' },
      { field: 'source', header: 'Source' },
      { field: 'attempts', header: 'Attempts' },
      // The reason a row failed is the point of exporting one.
      { field: 'lastError', header: 'Last error' },
      { field: 'createdAt', header: 'Queued' },
      { field: 'sentAt', header: 'Sent' },
    ],
    [],
  );

  const fetchExportData = useCallback(async () => sortedRows, [sortedRows]);

  return (
    <PermissionGuard permission="NOTIFICATION_MASTER.UPDATE" resourceName="Email Outbox">
      <Toast ref={toast} position="top-right" />
      <div className={embedded ? '' : 'p-6'} data-testid="SCR-EmailOutbox-List">
        <PageHeader
          embedded={embedded}
          icon="pi pi-send"
          title="Email Outbox"
          subtitle="Every message queued, sent, or failed"
          testId="EmailOutbox.Header"
          actions={
            <Button
              label="Compose"
              icon="pi pi-plus"
              size="small"
              onClick={() => navigate('/email/compose')}
              testId="EmailOutboxList.Button.Compose"
            />
          }
        />

        <div className={embedded ? '' : 'overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-gray-700 dark:bg-gray-800'}>
          <SaasDataTable<OutboxEntry>
            tableId="email-outbox"
            columns={columns}
            data={sortedRows}
            loading={loading}
            dataKey="id"
            emptyMessage="No email has been queued yet"
            first={first}
            rows={pageSize}
            totalRecords={total}
            onPage={(e: any) => {
              setFirst(e.first);
              setPageSize(e.rows);
            }}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sortField={sortField || undefined}
            sortOrder={sortOrder || undefined}
            onSort={(e: any) => {
              setSortField(e.sortField);
              setSortOrder(e.sortOrder);
            }}
            onFilter={() => undefined}
            globalSearch={search}
            onGlobalSearchChange={(value: string) => {
              setSearch(value);
              setFirst(0);
            }}
            globalSearchPlaceholder="Search by subject..."
            testId="EmailOutboxList"
            toolbarStart={
              <div className="flex flex-wrap items-center gap-2">
                <Dropdown
                  value={status}
                  options={[
                    { label: 'All statuses', value: '' },
                    ...EMAIL_STATUSES.map((s) => ({ label: s, value: s })),
                  ]}
                  onChange={(e: any) => {
                    setStatus(e.value);
                    setFirst(0);
                  }}
                  placeholder="All statuses"
                  style={{ width: '160px' }}
                  data-testid="EmailOutboxList.Filter.Status"
                  resetFilterOnHide
                />
              </div>
            }
            toolbarEnd={
              <>
                <span className="whitespace-nowrap text-sm text-ink-muted">
                  {total} {total === 1 ? 'record' : 'records'}
                </span>
                <ExportButton
                  fetchData={fetchExportData}
                  columns={exportColumns}
                  filename="email-outbox"
                  testId="EmailOutboxList.Button.Export"
                />
                <RefreshButton
                  onClick={() => void load()}
                  loading={loading}
                  testId="EmailOutboxList.Button.Refresh"
                />
              </>
            }
          />
        </div>
      </div>
    </PermissionGuard>
  );
}
